import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

/**
 * ProtectedRoute — guards routes that require a signed-in user.
 *
 * Owen Feb 28, 2026 (round 55) — fully reworked:
 *   Old version mounted on every route change and re-fired `/auth/me`,
 *   plus had an 8s timeout that bounced the user to landing if the
 *   network hiccupped. That was the "intermittent kick-out from Daily
 *   Journal" bug.
 *
 *   New version reads from AuthContext synchronously. The hydration
 *   round trip happens ONCE at app boot inside the context, not here.
 *   Route transitions are purely state-driven: no extra requests, no
 *   double-loaders, no timeouts. The only redirect-to-landing happens
 *   when the context has confirmed `status === 'unauthenticated'`
 *   (real 401, or no token marker present on initial boot).
 */
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const location = useLocation();
  const { user, status } = useAuth();

  // Hydration still in flight — show the spinner. NEVER redirect from
  // here; that path was the source of the kick-outs.
  if (status === 'pending') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F9F8F6]"
        data-testid="protected-route-loading"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  // Confirmed signed-out → land on the public homepage, preserving the
  // attempted destination so we could route back after sign-in.
  if (status === 'unauthenticated') {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  // Admin-gated page
  if (adminOnly && !user?.is_admin) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F9F8F6]"
        data-testid="admin-access-denied"
      >
        <div className="text-center">
          <h2
            className="text-2xl font-medium mb-2"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Access Denied
          </h2>
          <p className="text-[#57534E]">Admin access required</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
